import { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { toast } from 'components/global/CUK/Toast/Toast'
import { SSID } from 'models'
import { useSites } from 'store'

import useSelectedItems, { useHoveredItem } from '../store'
const LIMIT_SELECTED_ITEMS = 10

export interface CheckedItem extends SSID {
  color: string
  site_id?: string
}

const PALETTE = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD',
  '#98D8C8', '#FFA07A', '#87CEEB', '#DEB887', '#F0E68C', '#FFB6C1'
]

const useSelection = ({viewType, loading, ssids, sites}) => {
  const error = false
  const [checkedSSIDs, setCheckedSSIDs] = useState<CheckedItem[]>([])
  const [checkedSites, setCheckedSites] = useState<CheckedItem[]>([])
  const [hasInitializedSSIDs, setHasInitializedSSIDs] = useState(false)
  const [hasInitializedSites, setHasInitializedSites] = useState(false)
  const [hoveredItem, setHoveredItem] = useHoveredItem()
  const [, setSelectedItems] = useSelectedItems()
  const [{ selected: selectedSites }] = useSites()
  const { t } = useTranslation()

  const currentSiteIds = useMemo(() =>
    selectedSites?.map(site => site.sf_id).sort().join(',') || '', 
    [selectedSites]
  )

  useEffect(() => {
    setCheckedSSIDs([])
    setCheckedSites([])
    setHasInitializedSSIDs(false)
    setHasInitializedSites(false)
    setHoveredItem(null)
  }, [currentSiteIds])

  const items = useMemo(() => {
    if (viewType === 'site') {
      return sites
    } else if (viewType === 'evolution') {
      return []
    }
    return ssids
  }, [viewType, sites, ssids])

  const checked = useMemo(() => {
    if (viewType === 'site') {
      return checkedSites
    }
    return checkedSSIDs
  }, [viewType, checkedSites, checkedSSIDs])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const areItemsEqual = useCallback((item1: any, item2: any): boolean => {
    if (viewType === 'site') {
      return String(item1.name || '-') === String(item2.name || '-') && item1.site_id === item2.site_id
    }
    return String(item1.name || '-') === String(item2.name || '-') && item1.network_name === item2.network_name
  }, [viewType])

  const getAvailableColor = useCallback((currentChecked: CheckedItem[]) => {
    const usedColors = currentChecked.map(item => item.color)
    const availableColor = PALETTE.find(color => !usedColors.includes(color))
    return availableColor || PALETTE[currentChecked.length % PALETTE.length]
  }, [PALETTE])

  const reassignColors = useCallback((items: CheckedItem[]) => {
    return items.map((item, index) => ({
      ...item,
      color: PALETTE[index % PALETTE.length]
    }))
  }, [PALETTE])

  const setCheckedForViewType = useCallback((newChecked: CheckedItem[]) => {
    setSelectedItems(newChecked)
    if (viewType === 'site') {
      setCheckedSites(newChecked)
    } else {
      setCheckedSSIDs(newChecked)
    }
  }, [viewType])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onToggle = useCallback((item: any) => {
    const currentChecked = viewType === 'site' ? checkedSites : checkedSSIDs
    
    const normalizedItem = { ...item, name: item.name || '-' }
    const exists = currentChecked.find(checkedItem => areItemsEqual(normalizedItem, checkedItem))
    
    if (exists) {
      const filtered = currentChecked.filter(checkedItem => !areItemsEqual(normalizedItem, checkedItem))
      setCheckedForViewType(reassignColors(filtered))
    } else {
      if (currentChecked.length >= LIMIT_SELECTED_ITEMS) {
        toast.warning(
          t(viewType === 'site' ? 'stats.max_site_selection_reached_count' : 'stats.max_ssid_selection_reached_count', {
            count: LIMIT_SELECTED_ITEMS,
          })
        )
        return
      }
      
      const color = getAvailableColor(currentChecked)
      const newList = [...currentChecked, { 
        ...normalizedItem, 
        color
      } as CheckedItem]
      setCheckedForViewType(reassignColors(newList))
    }
  }, [areItemsEqual, t, getAvailableColor, reassignColors, viewType, checkedSSIDs, checkedSites, setCheckedForViewType])

  const onCheckTop = useCallback(() => {
    if (!items?.length) return
    
    const topItems = items
      .slice()
      .sort((a, b) => (b.total - (a.total)))
      .slice(0, LIMIT_SELECTED_ITEMS)
      .map((item, index) => ({
        ...item,
        name: item.name || '-',
        color: PALETTE[index % PALETTE.length]
      } as CheckedItem))
    
    setCheckedForViewType(topItems)
    
    if (viewType === 'site') {
      setHasInitializedSites(true)
    } else {
      setHasInitializedSSIDs(true)
    }
  }, [items, PALETTE, setCheckedForViewType, viewType])

  const onReset = useCallback(() => {
    setCheckedForViewType([])
    if (viewType === 'site') {
      setHasInitializedSites(false)
    } else {
      setHasInitializedSSIDs(false)
    }
  }, [setCheckedForViewType, viewType])

  const onToggleAll = useCallback(() => {
    if (!items?.length) return
    
    const currentChecked = viewType === 'site' ? checkedSites : checkedSSIDs
    
    const topItems = items
      .slice()
      .sort((a, b) => b.total - a.total)
      .slice(0, LIMIT_SELECTED_ITEMS)
    
    const allTopSelected = topItems.every(item => 
      currentChecked.some(checkedItem => areItemsEqual(item, checkedItem))
    )
    
    if (allTopSelected) {
      setCheckedForViewType([])
    } else {
      const newChecked = topItems.map((item, index) => ({
        ...item,
        name: item.name || '-',
        color: PALETTE[index % PALETTE.length]
      } as CheckedItem))
      setCheckedForViewType(newChecked)
    }
  }, [items, areItemsEqual, PALETTE, viewType, checkedSSIDs, checkedSites, setCheckedForViewType])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onMouseHover = useCallback((item: any) => {
    const normalizedItem = { ...item, name: item.name || '-' }
    const checkedItem = checked.find(checkedItem => areItemsEqual(normalizedItem, checkedItem))
    if (checkedItem) {
      setHoveredItem(checkedItem)
    }
  }, [checked, areItemsEqual])

  const onMouseOut = useCallback(() => {
    setHoveredItem(null)
  }, [])

  const checkedStatus = useMemo(() => {
    if (!items?.length) return 'none'
    
    const currentChecked = viewType === 'site' ? checkedSites : checkedSSIDs
    
    const filteredItems = items
      .filter(item => (item.upload || 0) > 0 || (item.download || 0) > 0)
      .map(item => ({ ...item, name: item.name || '-' }))
    
    const firstTenItems = filteredItems
      .slice()
      .sort((a, b) => ((b.upload || 0) + (b.download || 0)) - ((a.upload || 0) + (a.download || 0)))
      .slice(0, 10)
    
    const checkedFirstTen = firstTenItems.filter(item => 
      currentChecked.some(checkedItem => areItemsEqual(item, checkedItem))
    )
    
    if (checkedFirstTen.length === firstTenItems.length && firstTenItems.length > 0 && currentChecked.length === LIMIT_SELECTED_ITEMS) {
      return 'checked'
    } else if (checkedFirstTen.length > 0) {
      return 'indeterminate'
    } else {
      return 'none'
    }
  }, [checked, items, areItemsEqual, viewType, checkedSSIDs, checkedSites])

  useEffect(() => {
    const hasInitialized = viewType === 'site' ? hasInitializedSites : hasInitializedSSIDs
    
    if (!loading && 
        !hasInitialized &&
        (viewType === 'site' || viewType === 'ssid') && 
        items?.length > 0) {
      onCheckTop()
    }
  }, [loading, hasInitializedSSIDs, hasInitializedSites, viewType, items, onCheckTop])
  return useMemo(() => ({
    loading,
    error,
    ssids: ssids?.map(ssid => ({ ...ssid, name: ssid.name || '-' })) || [],
    sites: sites?.map(site => ({ ...site, name: site.name || '-' })) || [],
    items: items?.map(item => ({ ...item, name: item.name || '-' })) || [],
    checked,
    checkedStatus,
    hoveredItem,
    onToggle,
    onCheckTop,
    onReset,
    onToggleAll,
    onMouseHover,
    onMouseOut
  }), [
    loading, 
    error, 
    ssids,
    sites,
    items,
    checked,
    checkedStatus,
    hoveredItem,
    onToggle, 
    onCheckTop, 
    onReset, 
    onToggleAll,
    onMouseHover,
    onMouseOut
  ])
}

export default useSelection